/**
 * AFGCoin Chatbot Widget - Entry Point
 * This file makes the widget embeddable via a single script tag.
 * Uses Shadow DOM so host/WebView CSS can never affect the widget (permanent fix for white box).
 */
import React, { useState } from 'react';
import ReactDOM from 'react-dom/client';
import ChatbotWidget from './ChatbotWidget';
import stylesCss from './styles.css?raw';

// Wrapper component that manages remounting via key
function ChatbotWidgetWrapper({ apiUrl, position }) {
  const [chatKey, setChatKey] = useState(0);

  const handleClose = () => {
    // Increment key to force full component remount on next open
    // This ensures complete teardown and fresh state
    setChatKey(prev => prev + 1);
  };

  return (
    <ChatbotWidget 
      key={chatKey}
      apiUrl={apiUrl} 
      position={position}
      onCloseComplete={handleClose}
    />
  );
}

// Singleton guard to prevent multiple initializations
if (!window.__AFGCOIN_CHATBOT_INITIALIZED__) {
  window.__AFGCOIN_CHATBOT_INITIALIZED__ = false;
}

// Initialize widget when script is loaded
// CRITICAL: Assign to window immediately at top level (not inside conditional)
// This ensures the function exists even if React hasn't loaded yet
window.initAFGCoinChatbot = function(config = {}) {
  // Singleton guard: prevent multiple initializations
  if (window.__AFGCOIN_CHATBOT_INITIALIZED__) {
    console.warn('AFGCoin Chatbot: Widget has already been initialized. Ignoring duplicate initialization call.');
    return;
  }

  try {
    const {
      apiUrl = '',
      containerId = 'afgcoin-chatbot-container',
      position = 'bottom-right'
    } = config;

    // Require apiUrl - no default to prevent localhost connections
    if (!apiUrl) {
      console.error('AFGCoin Chatbot: apiUrl is required in config object');
      return;
    }

    // Ensure document.body exists
    if (!document.body) {
      console.error('AFGCoin Chatbot: document.body not available yet. Please call initAFGCoinChatbot after DOM is loaded.');
      return;
    }

    // Create container (host element) if it doesn't exist
    let container = document.getElementById(containerId);
    if (!container) {
      container = document.createElement('div');
      container.id = containerId;
      document.body.appendChild(container);
    }

    // Shadow DOM: host CSS cannot penetrate, so widget look is guaranteed (website + Android WebView)
    const shadowRoot = container.attachShadow({ mode: 'open' });

    const style = document.createElement('style');
    style.textContent = stylesCss;
    shadowRoot.appendChild(style);

    const innerRoot = document.createElement('div');
    innerRoot.className = 'afgcoin-chatbot-root';
    shadowRoot.appendChild(innerRoot);

    const root = ReactDOM.createRoot(innerRoot);
    root.render(
      <ChatbotWidgetWrapper apiUrl={apiUrl} position={position} />
    );

    // Mark as initialized after successful createRoot
    window.__AFGCOIN_CHATBOT_INITIALIZED__ = true;
  } catch (error) {
    console.error('AFGCoin Chatbot initialization error:', error);
  }
};

// Auto-initialize if data attributes are present
function autoInitialize() {
  // First try to get the current script tag (most reliable)
  let scriptTag = document.currentScript;
  
  // If currentScript is null (happens in some contexts), fall back to querySelector
  if (!scriptTag || !scriptTag.hasAttribute('data-afgcoin-chatbot')) {
    scriptTag = document.querySelector('script[data-afgcoin-chatbot]');
  }
  
  if (scriptTag && scriptTag.hasAttribute('data-afgcoin-chatbot')) {
    const apiUrl = scriptTag.getAttribute('data-api-url');
    if (!apiUrl) {
      console.error('AFGCoin Chatbot: data-api-url attribute is required. Please add data-api-url="https://your-api-url.com" to the script tag.');
      return;
    }
    window.initAFGCoinChatbot({ apiUrl });
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', autoInitialize);
} else {
  // DOM already loaded, run immediately
  autoInitialize();
}

